import { useClassName, useToggle } from '../../../helpers/hooks';
import {
  ComponentType,
  CSSProperties,
  ElementType,
  FC,
  ReactNode,
} from 'react';
import cx from 'classnames';
import collapseSVG from '../../../icons/list_collapse.svg';
import sortSVG from '../../../../../volto-list-component/src/icons/sort.svg';
import { Icon } from '@plone/volto/components';
import { Button, Table } from 'semantic-ui-react';

export interface ListProps {
  wrapperClassName?: string;
  headerColor: string;
  data: ListRow[];
  columns: ListColumnMetadata[];
  sort: (columnId: ListColumnMetadata['id']) => void;
  resultCount: number;
  batchingProps: BatchingProps;
  title: string;
  listId: string;
}

const List: FC<ListProps> = ({
  wrapperClassName: wrapperClassNameProp,
  headerColor,
  data,
  columns,
  sort,
  resultCount,
  batchingProps,
  title,
  listId,
}) => {
  const [collapsed, toggleCollapsed] = useToggle(false);
  const wrapperClassName = useClassName('list-component', wrapperClassNameProp);

  return (
    <>
      <div
        className={wrapperClassName}
        style={{ '--header-color': headerColor } as CSSProperties}
      >
        <div role="complementary">
          <Icon
            className="collapse"
            data-collapsed={collapsed}
            color={headerColor}
            size="1.3em"
            onClick={toggleCollapsed}
            name={collapseSVG}
          />
        </div>
        <Table
          unstackable
          basic="very"
          className={cx('baselist-component', {
            'is-empty': data?.length === 0,
          })}
          data-collapsed={collapsed}
        >
          <Table.Header>
            <Table.Row>
              {columns.map(({ HeaderComponent, ...column }) => (
                <Table.HeaderCell
                  key={column.id}
                  data-column={column.id}
                  className={column.className}
                  data-sortable={column.sortable}
                  data-sorted={column.sorted ?? false}
                  onClick={() => sort(column.id)}
                  data-col={column.id}
                >
                  {HeaderComponent != null ? (
                    <HeaderComponent
                      headerColor={headerColor}
                      columnData={column}
                      title={title}
                      count={resultCount}
                      listId={listId}
                    />
                  ) : (
                    <span className="wrapper">
                      <span className="column-title">{column.label}</span>
                      {column.sortable ? (
                        <Icon
                          className="sort-icon"
                          size="0.8em"
                          name={sortSVG}
                        />
                      ) : null}
                    </span>
                  )}
                </Table.HeaderCell>
              ))}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {data.length > 0 ? (
              data.map((row, idx) => (
                <Table.Row key={idx}>
                  {columns.map(({ CellComponent, ...col }) => (
                    <Table.Cell key={`${idx}-${col.id}`} data-col={col.id}>
                      {CellComponent != null ? (
                        <CellComponent
                          data={row[col.id]}
                          url={row.url}
                          id={col.id}
                          listId={listId}
                        />
                      ) : (
                        (row[col.id] as ReactNode)
                      )}
                    </Table.Cell>
                  ))}
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Cell colSpan={Object.keys(columns).length}>
                  <div className="empty-message text-muted text-center text-sm">
                    Kein Eintrag gefunden
                  </div>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
          {resultCount > data.length ? (
            <Table.Footer>
              <Table.Row>
                <Table.HeaderCell colSpan={columns.length}>
                  <Batching {...batchingProps} />
                </Table.HeaderCell>
              </Table.Row>
            </Table.Footer>
          ) : null}
        </Table>
      </div>
    </>
  );
};

export type ListRow = Record<string, unknown> & {
  url: string;
};

export type SortDirection = 'asc' | 'desc';

export interface HeaderComponentProps {
  headerColor: string;
  columnData: Omit<ListColumnMetadata, 'HeaderComponent' | 'CellComponent'>;
  title: string;
  count: number;
  listId: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface CellComponentProps<T = any> {
  id: string;
  url: string;
  data: T;
  listId: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type TCellComponent<T = any> = ComponentType<CellComponentProps<T>>;

export interface ListColumnMetadata {
  id: string;
  className?: string;
  label: string;
  HeaderComponent?: ElementType<HeaderComponentProps>;
  CellComponent?: TCellComponent;
  sortable: boolean;
  sorted?: SortDirection | false;
}

export interface BatchingProps {
  batchSize: number;
  addBatchSize: () => void;
  loadAll: () => void;
}

export const Batching: FC<BatchingProps> = ({
  batchSize,
  addBatchSize,
  loadAll,
}) => (
  <div className="batching">
    Mehr Ergebnisse
    <Button
      className="link"
      name={`expand-${batchSize}`}
      onClick={addBatchSize}
    >
      +{batchSize}
    </Button>
    /
    <Button className="link" name="expand-all" onClick={loadAll}>
      Alle
    </Button>
  </div>
);

export default Object.assign(List, {
  Batching,
});
